/**
 * Single source of truth for rendering an event's cost. Ensures a `$` is
 * always visible when the event is paid, regardless of which field the
 * host/admin populated.
 *
 * Priority:
 *   1. If `cost_amount` is a positive number (admin-written), render
 *      `$X.XX` — authoritative.
 *   2. If `cost_description` already contains a `$`, render it verbatim
 *      (host wrote something like "$10 entry fee" or "$15 — covers tea").
 *   3. If `cost_description` is a bare number like "10" or "10.50", prefix
 *      it with `$`.
 *   4. Otherwise, return the description verbatim (handles non-numeric
 *      strings like "Covers tea and snacks") and fall through to "Paid"
 *      if it's empty.
 */
export function formatEventCost(event) {
  if (!event) return 'Paid';
  if (event.is_free) return 'Free';

  const amt = Number(event.cost_amount);
  if (Number.isFinite(amt) && amt > 0) {
    // Drop trailing zeros for whole dollars ($25 instead of $25.00) but
    // keep cents when they matter ($25.50).
    return amt % 1 === 0 ? `$${amt}` : `$${amt.toFixed(2)}`;
  }

  const desc = (event.cost_description || '').trim();
  if (!desc) return 'Paid';
  if (desc.includes('$')) return desc;

  // Bare-number descriptions ("10" or "10.50") get a $ prefix.
  const bareNumber = /^\d+(\.\d+)?$/.test(desc);
  if (bareNumber) return `$${desc}`;

  // Anything else is a human-written free-form description — let it be.
  return desc;
}
